import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { listIngredients } from '../api/ingredients.js';
import type { Category, Ingredient } from '../api/types.js';
import { useToast } from '../hooks/useToast.js';
import { IngredientDialog } from './recipes/IngredientDialog.js';

const CATEGORY_ICON: Record<Category, string> = {
  Produce: '🥬',
  Meat: '🥩',
  Dairy: '🧀',
  'Dry Goods': '🌾',
  Spices: '🌶️',
  Beverages: '🥤',
  Other: '📦',
};

export function Ingredients(): ReactElement {
  const toast = useToast();
  const [items, setItems] = useState<Ingredient[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Ingredient | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 250);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listIngredients({ search: debouncedSearch || undefined })
      .then((rows) => {
        if (cancelled) return;
        const sorted = [...rows].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
        setItems(sorted);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load ingredients';
        toast.push('error', msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, refreshKey, toast]);

  function openNew(): void {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(item: Ingredient): void {
    setEditing(item);
    setDialogOpen(true);
  }

  return (
    <div className="flex flex-col gap-3 p-4" data-testid="ingredients-view">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">🥕 Ingredients</h1>
        <button
          type="button"
          onClick={openNew}
          className="rounded-md border px-3 py-2 text-sm font-medium"
          style={{
            background: 'var(--accent)',
            borderColor: 'var(--accent)',
            color: 'var(--bg-primary, #fff)',
            minHeight: 40,
          }}
          data-testid="ingredients-new"
        >
          + New
        </button>
      </header>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 Search ingredients..."
        className="w-full rounded-md border px-3 py-2 text-sm"
        style={{
          background: 'var(--bg-primary, transparent)',
          borderColor: 'var(--border)',
          color: 'var(--text-main)',
        }}
        data-testid="ingredients-search"
      />

      <div className="flex flex-col gap-2" data-testid="ingredients-list">
        {loading && items.length === 0 ? (
          <div
            className="rounded-xl border p-6 text-center text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
            }}
          >
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-xl border p-6 text-center text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
            }}
            data-testid="ingredients-empty"
          >
            {debouncedSearch.length > 0
              ? 'No ingredients match your search.'
              : 'No ingredients yet. Tap + New to create one.'}
          </div>
        ) : (
          items.map((i) => (
            <button
              key={i.id}
              type="button"
              onClick={() => openEdit(i)}
              className="flex items-center justify-between gap-3 rounded-xl border p-3 text-left"
              style={{
                borderColor: 'var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-main)',
                minHeight: 56,
              }}
              data-testid={`ingredients-row-${i.id}`}
            >
              <div className="flex min-w-0 flex-col gap-1">
                <span className="truncate text-sm font-medium">{i.name}</span>
                <span
                  className="flex flex-wrap items-center gap-1 text-xs"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <span aria-label={i.category} title={i.category}>
                    {CATEGORY_ICON[i.category]}
                  </span>
                  <span>{i.kcal_per_100} kcal / 100{i.default_unit}</span>
                </span>
              </div>
              <span
                aria-hidden="true"
                style={{ color: 'var(--text-muted)' }}
              >
                ✏️
              </span>
            </button>
          ))
        )}
      </div>

      <IngredientDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editing={editing}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}